// L'assurance-vie : un fonds euros garanti et des unités de compte (les fonds du marché), dans un contrat
// en ligne ou dans celui de la banque. Règles simplifiées de 2026 :
// - Pas de plafond de versements, et l'argent reste disponible à tout moment.
// - Le fonds euros rapporte le taux annoncé chaque janvier, net des frais du contrat ; ses intérêts paient
//   17,2 % de prélèvements sociaux au moment où ils sont versés, le 31 décembre.
// - Au retrait, seuls les gains sont imposés : 12,8 % avant huit ans ; après, 4 600 € de gains par an
//   sans impôt, puis 7,5 %. Les gains qui n'ont pas encore payé les prélèvements sociaux (ceux des unités
//   de compte, vendues ou non) les paient à ce moment-là.
// - Les unités de compte paient des frais de gestion chaque mois, pris sur le fonds euros.
import { round2 } from '../engine/finance';
import { fmtEur, fmtEurPrecise, fmtPct } from '../engine/format';
import type { AssetId, AvContract, GameState } from '../engine/state';
import { DAYS_PER_YEAR, dateAtDay } from '../engine/time';
import { addJournal, addNotice, payFromLivret } from './actions';
import { priceOf, scheduleFirstDrop } from './market';

export const AV = {
  contracts: {
    // Un contrat en ligne : pas de frais d'entrée, des frais de gestion bas.
    online: { entryFee: 0, fee: 0.006, eurosBonus: 0 },
    // Celui de la banque : des frais d'entrée, des frais de gestion plus hauts, un fonds euros un peu moins payé.
    bank: { entryFee: 0.03, fee: 0.009, eurosBonus: -0.003 },
  } satisfies Record<AvContract, { entryFee: number; fee: number; eurosBonus: number }>,
  socialTax: 0.172,
  incomeTaxBefore: 0.128,
  incomeTaxAfter: 0.075,
  /** Les gains retirés sans impôt chaque année après huit ans (pour une personne seule). */
  allowance: 4600,
  years: 8,
  /** Les unités de compte possibles : les fonds du marché, pas les actions. */
  ucAssets: ['monde', 'maison', 'oblig', 'robotique'] as AssetId[],
  resultsKept: 12,
};

/** Le taux du fonds euros de l'année pour ce contrat, net de ses frais. */
export function eurosRate(state: GameState): number {
  return eurosRateFor(state, state.av.contract);
}

export function avUcValue(state: GameState): number {
  return Object.entries(state.av.holdings).reduce(
    (sum, [id, holding]) => sum + (holding?.shares ?? 0) * priceOf(state, id as AssetId),
    0,
  );
}

/** Les intérêts de l'année du fonds euros, moins les prélèvements sociaux qu'ils paieront le 31 décembre. */
function accruedNet(state: GameState): number {
  return state.av.eurosAccrued * (1 - AV.socialTax);
}

/**
 * Ce que vaut le contrat : le fonds euros, ses intérêts de l'année (nets des prélèvements sociaux, pour que
 * leur versement ne fasse pas chuter la valeur), et les unités de compte au prix du jour.
 */
export function avValue(state: GameState): number {
  return state.av.euros + accruedNet(state) + avUcValue(state);
}

/** Le taux du fonds euros de l'année pour un contrat qu'on n'a pas encore ouvert, pour comparer. */
export function eurosRateFor(state: GameState, contract: AvContract): number {
  return Math.max(0, state.market.eurosRate + AV.contracts[contract].eurosBonus);
}

/** L'âge du contrat en années : à huit ans, ses gains retirés paient beaucoup moins d'impôt. */
export function avAgeYears(state: GameState): number {
  return state.av.open ? (state.day - state.av.openDay) / DAYS_PER_YEAR : 0;
}

/** On peut ouvrir une assurance-vie dès le premier emploi ; le jeu n'en garde qu'une à la fois. */
export function canOpenAv(state: GameState): boolean {
  return !state.av.open && state.firstJobDay !== null;
}

export function openAv(state: GameState, contract: AvContract): void {
  if (!canOpenAv(state)) return;
  state.av = {
    ...state.av,
    open: true,
    openDay: state.day,
    contract,
    euros: 0,
    eurosAccrued: 0,
    deposits: 0,
    holdings: {},
    taxedGains: 0,
    valueAtMonthStart: 0,
    flows: 0,
    lastMonthResult: 0,
    results: [],
    history: [],
    historyWeek: -1,
  };
  addJournal(state, contract === 'online' ? 'journal.avOpenOnline' : 'journal.avOpenBank');
}

/**
 * Un versement : il arrive sur le fonds euros, moins les frais d'entrée du contrat (ou ceux accordés par le
 * conseiller). Il vient du compte courant, ou des livrets. Renvoie ce qui a été versé.
 */
export function depositToAv(
  state: GameState,
  amount: number,
  source: 'cash' | 'livret' = 'cash',
  entryFee: number = AV.contracts[state.av.contract].entryFee,
): number {
  if (!state.av.open) return 0;
  const available = source === 'cash' ? Math.max(0, state.cash) : state.livret + state.ldds + state.lep;
  const value = round2(Math.min(amount, available));
  if (value <= 0) return 0;
  if (source === 'cash') {
    state.cash = round2(state.cash - value);
    state.year.saved += value;
  } else {
    payFromLivret(state, value);
  }
  const fee = round2(value * entryFee);
  state.av.euros = round2(state.av.euros + value - fee);
  state.av.deposits = round2(state.av.deposits + value);
  state.av.flows = round2(state.av.flows + value);
  return value;
}

/** Le nombre de parts d'une unité de compte qu'on peut acheter avec le fonds euros. */
export function maxUc(state: GameState, asset: AssetId): number {
  if (!state.av.open || !AV.ucAssets.includes(asset)) return 0;
  return Math.floor(state.av.euros / priceOf(state, asset));
}

/** Acheter des parts d'unités de compte avec l'argent du fonds euros (sans frais dans le contrat). */
export function buyUc(state: GameState, asset: AssetId, count: number): number {
  const shares = Math.min(Math.floor(count), maxUc(state, asset));
  if (shares <= 0) return 0;
  const cost = round2(shares * priceOf(state, asset));
  const holding = state.av.holdings[asset] ?? { shares: 0, cost: 0 };
  state.av.holdings = { ...state.av.holdings, [asset]: { shares: holding.shares + shares, cost: round2(holding.cost + cost) } };
  state.av.euros = round2(state.av.euros - cost);
  scheduleFirstDrop(state);
  return shares;
}

/** Vendre des parts : l'argent revient sur le fonds euros, sans impôt tant qu'on ne le retire pas. */
export function sellUc(state: GameState, asset: AssetId, count: number): number {
  const holding = state.av.holdings[asset];
  if (!holding) return 0;
  const shares = Math.min(Math.floor(count), holding.shares);
  if (shares <= 0) return 0;
  const left = holding.shares - shares;
  const holdings = { ...state.av.holdings };
  if (left > 0) holdings[asset] = { shares: left, cost: round2((holding.cost * left) / holding.shares) };
  else delete holdings[asset];
  state.av.holdings = holdings;
  state.av.euros = round2(state.av.euros + shares * priceOf(state, asset));
  return shares;
}

/** L'abattement de l'année : les gains déjà retirés sans impôt depuis janvier. */
function allowanceLeft(state: GameState): number {
  const year = dateAtDay(state.startDate, state.day).getFullYear();
  if (state.av.allowanceYear !== year) return AV.allowance;
  return Math.max(0, AV.allowance - state.av.allowanceUsed);
}

/**
 * Un retrait emporte une part de chaque chose du contrat : de ses versements, et de ses gains. Seule la part
 * de gains est imposée ; les gains déjà passés par les prélèvements sociaux ne les paient pas deux fois.
 */
function withdrawalShares(state: GameState, withdrawn: number): { share: number; gains: number; untaxed: number } {
  const value = avValue(state);
  const share = value > 0 ? withdrawn / value : 0;
  const gains = share * Math.max(0, value - state.av.deposits);
  // Les intérêts de l'année paieront les leurs le 31 décembre.
  const untaxed = share * Math.max(0, value - state.av.deposits - state.av.taxedGains - accruedNet(state));
  return { share, gains, untaxed };
}

/** Les impôts d'un retrait du fonds euros : sur la part de gains qu'il emporte. */
export function avWithdrawalPreview(state: GameState, amount: number): { amount: number; tax: number } {
  const withdrawn = round2(Math.min(amount, state.av.euros));
  if (withdrawn <= 0 || avValue(state) <= 0) return { amount: 0, tax: 0 };
  const { gains, untaxed } = withdrawalShares(state, withdrawn);
  const income =
    avAgeYears(state) < AV.years
      ? gains * AV.incomeTaxBefore
      : Math.max(0, gains - allowanceLeft(state)) * AV.incomeTaxAfter;
  return { amount: withdrawn, tax: round2(untaxed * AV.socialTax + income) };
}

/** Retirer de l'argent du fonds euros vers le compte courant (des unités de compte, on les vend d'abord au Marché). */
export function withdrawFromAv(state: GameState, amount: number): void {
  const { amount: withdrawn, tax } = avWithdrawalPreview(state, amount);
  if (withdrawn <= 0) return;
  const { share, gains } = withdrawalShares(state, withdrawn);
  // Après huit ans, les gains retirés entament l'abattement de l'année.
  if (avAgeYears(state) >= AV.years) {
    const year = dateAtDay(state.startDate, state.day).getFullYear();
    const used = state.av.allowanceYear === year ? state.av.allowanceUsed : 0;
    state.av.allowanceYear = year;
    state.av.allowanceUsed = round2(Math.min(AV.allowance, used + gains));
  }
  // Le reste du contrat garde ses proportions : versements et gains déjà taxés baissent d'autant.
  state.av.deposits = round2(state.av.deposits * (1 - share));
  state.av.taxedGains = round2(state.av.taxedGains * (1 - share));
  state.av.euros = round2(state.av.euros - withdrawn);
  state.av.flows = round2(state.av.flows - withdrawn);
  state.cash = round2(state.cash + withdrawn - tax);
  state.year.saved -= withdrawn;
  addJournal(state, 'journal.avWithdraw', { amount: fmtEur(withdrawn), tax: fmtEurPrecise(tax) });
}

/** Chaque jour, les intérêts du fonds euros s'ajoutent à ceux de l'année. */
export function accrueAvInterest(state: GameState, days: number): void {
  if (!state.av.open || state.av.euros <= 0) return;
  state.av.eurosAccrued += (state.av.euros * eurosRate(state) * days) / DAYS_PER_YEAR;
}

/** Le 31 décembre, les intérêts de l'année arrivent sur le fonds euros, moins 17,2 % de prélèvements sociaux. */
export function creditAvInterest(state: GameState): void {
  if (!state.av.open) return;
  const gross = round2(state.av.eurosAccrued);
  state.av.eurosAccrued = 0;
  if (gross <= 0) return;
  const social = round2(gross * AV.socialTax);
  state.av.euros = round2(state.av.euros + gross - social);
  state.av.taxedGains = round2(state.av.taxedGains + gross - social);
  state.totalInterest += gross - social;
  addJournal(state, 'journal.avInterest', { gross: fmtEur(gross), social: fmtEur(social) });
}

/** L'annonce du taux du fonds euros de l'année, en janvier. */
export function announceEurosRate(state: GameState): void {
  if (!state.av.open) return;
  const params = { rate: fmtPct(eurosRate(state)) };
  addNotice(state, 'av.rate', params);
  addJournal(state, 'av.rate', params);
}

export function avAverageResult(state: GameState): number {
  const results = state.av.results;
  return results.length > 0 ? results.reduce((sum, value) => sum + value, 0) / results.length : 0;
}

/**
 * Chaque mois : les frais de gestion des unités de compte, pris sur le fonds euros (s'il n'y suffit pas,
 * l'assureur vend une part), puis le résultat du mois, hors versements et retraits.
 */
export function avMonth(state: GameState): void {
  const av = state.av;
  if (!av.open) return;
  const fee = round2((avUcValue(state) * AV.contracts[av.contract].fee) / 12);
  if (fee > av.euros) {
    const held = (Object.keys(av.holdings) as AssetId[]).sort((a, b) => priceOf(state, a) - priceOf(state, b));
    if (held.length > 0) sellUc(state, held[0], 1);
  }
  av.euros = round2(Math.max(0, av.euros - fee));
  const value = avValue(state);
  const result = round2(value - av.valueAtMonthStart - av.flows);
  av.lastMonthResult = result;
  av.results = [...av.results, result].slice(-AV.resultsKept);
  av.flows = 0;
  av.valueAtMonthStart = value;
}

/** Chaque semaine de jeu, un point de la courbe du contrat. */
export function recordAvHistory(state: GameState): void {
  const av = state.av;
  const week = Math.floor(state.day / 7);
  if (!av.open || week <= av.historyWeek) return;
  av.historyWeek = week;
  av.history = [...av.history, { value: round2(avValue(state)), deposits: round2(av.deposits) }];
}

/** Des parts en bourse, au PEA ou en unités de compte : les cartes de la bourse s'adressent à qui en a. */
export function hasMarketShares(state: GameState): boolean {
  return Object.keys(state.pea.holdings).length > 0 || Object.keys(state.av.holdings).length > 0;
}
